import { defineStore } from "pinia";
import { AnalyticsV2ElementType } from "@/models/etc/types";
import CommonSelector from "@/models/base/common.selector";
import { reactive } from "vue";
import { generalStore } from "./general";
import { storageStore } from "./storage/storage-instances";

/**
 * global segment and authentication store
 */

const API_URL = process.env.VUE_APP_API_URL

const LAST_SEGMENT_STORAGE_KEY = "analytics.lastSegment";
// Последний нескладской сегмент: на него ведёт пункт «Аналитика» в шапке,
// тогда как «Склад» всегда открывает позиции склада.
const LAST_ANALYTICS_SEGMENT_STORAGE_KEY = "analytics.lastAnalyticsSegment";

export const STORAGE_SEGMENTS: number[] = [
  AnalyticsV2ElementType.StorageItem,
  AnalyticsV2ElementType.StorageItemMovement,
];

// Права, от которых зависит состав сегментов Аналитики.
export type SegmentAccess = {
  analytics: boolean;
  storage: boolean;
  workload: boolean;
};

// Полный набор сегментов: до загрузки прав и для проверки, что сегмент вообще существует.
const FULL_SEGMENT_ACCESS: SegmentAccess = { analytics: true, storage: true, workload: true };

export type RootState = {
    jwtToken: string;
    mode: number;
    lastAnalyticsMode: number;
    generalSelector: CommonSelector[];

    // for old API
    sessionKey: string;
    userID: string;
};

/**
 * elementName здесь — КЛЮЧ i18n, а не готовый перевод. Селектор собирается по правам
 * доступа (в applySegmentAccess) и живёт в state строками, а язык приходит из профиля
 * пользователя позже — если переводить заранее, названия сегментов навсегда застывали бы
 * на локали, активной в момент сборки. Перевод делает потребитель при отрисовке
 * (segment.selector), поэтому названия следуют за языком сами.
 *
 * Без доступа к аналитике, но с доступом к складу в селекторе остаются только
 * складские сегменты.
 */
function buildGeneralSelector(access: SegmentAccess): CommonSelector[] {
  const selector: CommonSelector[] = [];

  if (access.analytics) {
    selector.push(
      new CommonSelector(AnalyticsV2ElementType.Project, 'projects_label'),
      new CommonSelector(AnalyticsV2ElementType.Client, 'clients_label'),
      new CommonSelector(AnalyticsV2ElementType.Job, 'jobs_label'),
      new CommonSelector(AnalyticsV2ElementType.Person, 'contacts_label'),
      new CommonSelector(AnalyticsV2ElementType.File, 'files_label'),
      new CommonSelector(AnalyticsV2ElementType.Tag, 'tags_upcase'),
    );

    if (access.workload) {
      selector.push(new CommonSelector(AnalyticsV2ElementType.Workload, 'load_upcase'));
    }
  }

  // Storage segments are always at the bottom of regular segments.
  if (access.storage) {
    selector.push(
      new CommonSelector(AnalyticsV2ElementType.StorageItem, 'segment_storage_positions'),
      new CommonSelector(AnalyticsV2ElementType.StorageItemMovement, 'segment_storage_actions'),
    );
  }

  if (access.analytics) {
    selector.push(new CommonSelector(AnalyticsV2ElementType.Report, 'reports_label'));
  }
  return selector;
}

const isKnownAnalyticsSegment = (mode: number): boolean =>
  !STORAGE_SEGMENTS.includes(mode) &&
  buildGeneralSelector(FULL_SEGMENT_ACCESS).some(s => s.id === mode);

// До появления отдельного ключа помнили только общий последний сегмент:
// берём его, если он нескладской, чтобы «Аналитика» не сбрасывала всех на Проекты.
function readLastAnalyticsMode(): number {
  if (typeof window === "undefined") { return AnalyticsV2ElementType.Project }
  try {
    const raw = window.localStorage.getItem(LAST_ANALYTICS_SEGMENT_STORAGE_KEY)
      ?? window.localStorage.getItem(LAST_SEGMENT_STORAGE_KEY);
    if (raw === null) { return AnalyticsV2ElementType.Project }
    const saved = parseInt(raw);
    return isKnownAnalyticsSegment(saved) ? saved : AnalyticsV2ElementType.Project;
  } catch (e) {
    return AnalyticsV2ElementType.Project;
  }
}

export const rootStore = defineStore("rootStore", {
  state: () =>
    ({
        jwtToken: '',
        mode: 0,
        lastAnalyticsMode: readLastAnalyticsMode(),
        sessionKey: '',
        userID: '',
        generalSelector: reactive(buildGeneralSelector(FULL_SEGMENT_ACCESS) as CommonSelector[])
    } as RootState),

  actions: {
    // set auth token got from the main client
    async setJWT(payload: string, skey: string, uid: string) {
      this.jwtToken = payload

      this.sessionKey = skey;
      this.userID = uid;

      // check read/write access rights
      const genstore = generalStore()
      const storage = storageStore()

      await genstore.checkGeneralAccess()
      await storage.getStorageAccess()

      console.log('wload access: '+genstore.accessRights.AccessToWorkload)
      console.log('store access: '+storage.storageAccess?.CanRead)

      this.applySegmentAccess({
        analytics: !!genstore.accessRights.GeneralAccess,
        storage: !!storage.storageAccess?.CanRead,
        workload: !!genstore.accessRights.AccessToWorkload,
      })
    },

    // Собирает селектор сегментов по правам. Восстановленный или пришедший ссылкой
    // сегмент может оказаться недоступным — тогда переходим на первый доступный
    // (Проекты, а без доступа к аналитике — позиции склада). Без прав совсем селектор
    // пуст, сегмент не трогаем: страница всё равно покажет «Нет доступа».
    applySegmentAccess(access: SegmentAccess) {
      this.$state.generalSelector = reactive(buildGeneralSelector(access) as CommonSelector[])

      const selector = this.$state.generalSelector
      if (selector.length && !selector.some(s => s.id === this.mode)) {
        this.setGeneralMode(selector[0].id)
      }

      // Иначе «Аналитика» в шапке вела бы на сегмент, к которому доступа уже нет.
      const analyticsSegment = selector.find(s => !STORAGE_SEGMENTS.includes(s.id))
      if (analyticsSegment && !selector.some(s => s.id === this.lastAnalyticsMode)) {
        this.lastAnalyticsMode = analyticsSegment.id
        this.persistGeneralMode()
      }
    },

    setToken(payload: string) {
      this.jwtToken = payload
    },

    // global segment (same as Destination)
    setGeneralMode(payload: number) {
      this.mode = payload
      if (!STORAGE_SEGMENTS.includes(payload)) {
        this.lastAnalyticsMode = payload
      }
      this.persistGeneralMode()
    },

    // Последний открытый класс аналитики живёт между перезагрузками страницы.
    persistGeneralMode() {
      if (typeof window === "undefined") { return }
      try {
        window.localStorage.setItem(LAST_SEGMENT_STORAGE_KEY, String(this.mode))
        window.localStorage.setItem(LAST_ANALYTICS_SEGMENT_STORAGE_KEY, String(this.lastAnalyticsMode))
      } catch (e) {
        // приватный режим браузера: работаем без запоминания
      }
    },

    // Возвращает true, если сохранённый сегмент прочитан и применён.
    initGeneralModeFromStorage(): boolean {
      if (typeof window === "undefined") { return false }
      try {
        const raw = window.localStorage.getItem(LAST_SEGMENT_STORAGE_KEY)
        if (raw === null) { return false }
        const saved = parseInt(raw)
        if (isNaN(saved) || !buildGeneralSelector(FULL_SEGMENT_ACCESS).some(s => s.id === saved)) {
          return false
        }
        this.mode = saved
        return true
      } catch (e) {
        return false
      }
    },
  },

  getters: {
    // jwt auth token
    globalJWT(state): string {
      return state.jwtToken
    },

    // jwt auth token
    oldAPISessionKey(state): string {
      return state.sessionKey
    },

    // jwt auth token
    oldAPIUID(state): string {
      return state.userID
    },

    // global segment getter
    generalMode(state): number {
      return state.mode
    },

    isStorageMode(state): boolean {
      return STORAGE_SEGMENTS.includes(state.mode)
    }
  }
});
